import { Box, useTheme } from '@mui/material';
import { Accordion, AccordionDetails, AccordionSummary } from '@portals/core/src/components/Accordion/Accordion';
import { Spacer } from '@portals/core/src/components/Spacer/Spacer';
import { TooltipButton } from '@portals/core/src/components/TooltipButton/TooltipButton';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import type { Theme } from '@portals/core/src/themes/themes';
import { ChevronDownSmall } from '@portals/icons/src/ChevronDownSmall/ChevronDownSmall';
import Image from 'next/image';
import React, { useCallback, useState } from 'react';

import { InternQuestion, InternText } from '../../../../../models';
import { Consumer } from '../../../../consumers/Consumer/Consumer';
import { getStyles } from './Question.styles';

export interface QuestionProps extends InternQuestion {
  questionNumber: number;
  displayNumbers: boolean;
}

export const Question: React.FC<QuestionProps> = ({
  questionNumber,
  question,
  answer,
  media,
  expanded: defaultExpanded,
  displayNumbers,
}: QuestionProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const onChange = useCallback((event, isExpanded) => {
    setExpanded(isExpanded);
  }, []);
  const theme = useTheme<Theme>();
  const styles = getStyles(theme, { expanded, displayNumbers });

  return (
    <Accordion
      disableGutters={true}
      key={`Question-${questionNumber}`}
      defaultExpanded={defaultExpanded}
      onChange={onChange}
    >
      <AccordionSummary
        aria-controls={`panel${questionNumber}-content`}
        id={`panel${questionNumber}-header`}
        expandIcon={<ChevronDownSmall />}
        sx={styles.summary}
      >
        <Box display="flex" alignItems="center" gap={5} flexGrow={1}>
          {displayNumbers && (
            <Typography sx={styles.questionNumber} variant="h6" component="span">
              {questionNumber}
            </Typography>
          )}
          <Typography variant="body1" component={question.component} fontWeight="bold">
            {question.text}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={styles.details}>
        <Box display="flex" justifyContent="space-between" gap={3} alignItems="flex-start">
          <Box>
            <Consumer node={{ ...answer, innerPadding: 'vertical' } as InternText} />
          </Box>
          {answer.tooltip ? (
            <TooltipButton
              sx={{ ml: theme.spacing(1) }}
              title={<Consumer node={{ __component: 'intern.text', text: answer.tooltip } as InternText} />}
            />
          ) : null}
        </Box>

        {media ? (
          <>
            <Spacer />
            <Image
              src={media.url}
              width={media.width}
              height={media.height}
              title={media.title}
              alt={media.alternativeText ? media.alternativeText : null}
              style={{
                marginLeft: 'auto',
                marginRight: 'auto',
                display: 'block',
                maxWidth: '100%',
                height: 'auto',
              }}
            />
          </>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
};
